/* eslint-disable indent */
/* eslint-disable no-shadow */
import '../style.scss';

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { getCouncilsByUserId } from 'apis/statistics.api';

import Pagination from 'components/Pagination';
import { mapError } from 'utils/func';
import { COUNCIL_TYPE_CODE } from 'constant';

function CouncilsTable({ userId }) {
  // #region Data
  const { t } = useTranslation();
  const location = useLocation();

  // Pagination state
  const [pagination, setPagination] = useState({
    first: 0,
    rowsPerPage: 10,
  });

  // Lấy filter từ localStorage khi load trang
  useEffect(() => {
    // Lấy rowsPerPage từ localStorage (nếu có)
    const localRowsPerPage = parseInt(
      localStorage.getItem(`council-joined-${location.pathname.slice(1)}-rowsPerPage`),
      10
    );
    if (localRowsPerPage) {
      setPagination((cur) => ({ ...cur, rowsPerPage: localRowsPerPage }));
    }

    // Lấy first từ localStorage (không từ URL)
    const localFirst = parseInt(
      localStorage.getItem(`council-joined-${location.pathname.slice(1)}-first`) || '0',
      10
    );
    setPagination((cur) => ({ ...cur, first: localFirst }));
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem(`council-joined-${location.pathname.slice(1)}-first`, pagination.first);
  }, [pagination.first, location.pathname]);

  const {
    data: _data,
    isLoading,
    isError,
    error,
  } = useQuery(['council-joined', userId, pagination], ({ queryKey: [, _userId, _pagination] }) =>
    getCouncilsByUserId(_userId, _pagination)
  );
  const data = useMemo(
    () => _data?.data ?? { data: [], pagination: { row: 10, totalRecords: 0 } },
    [_data]
  );

  // #endregion

  const formatCouncilName = (value) => {
    if (value?.council?.councilType?.code === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL) {
      return (
        <Link
          to={`/projects-revise/${value?.projectProposalCouncils?.[0]?.projectProposal?.id}?tab=2`}
          className="hover:underline"
        >
          {value?.council?.name}
        </Link>
      );
    }
    if (value?.council?.councilType?.code === COUNCIL_TYPE_CODE.FINANCE_COUNCIL) {
      return (
        <Link
          to={`/projects-revise/${value?.projectProposalCouncils?.[0]?.projectProposal?.id}?tab=3`}
          className="hover:underline"
        >
          {value?.council?.name}
        </Link>
      );
    }
    if (value?.council?.councilType?.code === COUNCIL_TYPE_CODE.MIDTERM_REPORT_COUNCIL) {
      return (
        <Link
          to={`/projects-submit/${value?.projectId}/${value?.projectProposalCouncils?.[0]?.projectProposal?.id}/${value?.council?.summaryReport?.id}/midterm-document?tab=2`}
          className="hover:underline"
        >
          {value?.council?.name}
        </Link>
      );
    }
    if (value?.council?.councilType?.code === COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL) {
      return (
        <Link
          to={`/projects-submit/${value?.projectId}/${value?.projectProposalCouncils?.[0]?.projectProposal?.id}/${value?.council?.summaryReport?.id}/acceptance-document?tab=2`}
          className="hover:underline"
        >
          {value?.council?.name}
        </Link>
      );
    }
    return (
      <Link to={`/councils/${value?.id}?`} className="hover:underline">
        {value?.council?.name}
      </Link>
    );
  };

  const formatName = (value) => {
    const link = `/projects-revise/${value?.reference}?tab=0`;

    return (
      <Link to={link} className="hover:underline">
        {value?.vietnameseName}
      </Link>
    );
  };

  const formatOwnerName = (value) => value?.projectProposalContacts?.[0]?.fullname;

  const formatCouncilType = (value) =>
    t(
      `council.view.councilType.${value?.projectProposalCouncils?.[0]?.council?.councilType?.code}`
    );

  const formatProgram = (value) => value?.program?.name ?? '';
  // #endregion

  return (
    <div className="grid">
      <div className="col-12">
        {isError && <h3 className="text-red-600">{mapError(t, error)}</h3>}

        <DataTable
          value={data?.data ?? []}
          className="p-datatable-gridlines header-table"
          showGridlines
          dataKey="key"
          loading={isLoading}
          emptyMessage={t('statistic.emptyMessage')}
        >
          <Column
            field="key"
            header={t('statistic.no')}
            headerStyle={{ justifyContent: 'center' }}
            style={{ minWidth: '2rem', textAlign: 'center' }}
          />
          <Column
            header={t('statistic.councilName')}
            style={{ minWidth: '16rem' }}
            body={formatCouncilName}
          />
          <Column
            header={t('statistic.projectName')}
            style={{ minWidth: '16rem' }}
            body={formatName}
          />
          <Column
            header={t('statistic.ownerName')}
            style={{ minWidth: '8rem' }}
            body={formatOwnerName}
          />
          <Column
            header={t('statistic.program')}
            style={{ minWidth: '8rem' }}
            body={formatProgram}
          />
          <Column
            header={t('statistic.councilType')}
            style={{ minWidth: '8rem' }}
            body={formatCouncilType}
          />
        </DataTable>

        <Pagination
          pagination={pagination}
          setPagination={setPagination}
          paginationData={data.pagination}
          pageName="scientist-project-proposal-defense"
        />
      </div>
    </div>
  );
}

CouncilsTable.propTypes = {
  userId: PropTypes.string,
};

CouncilsTable.defaultProps = {
  userId: null,
};

export default CouncilsTable;
